import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import 'moment/locale/es';
import { useAuth } from '../auth/AuthContext';
import api from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarDays, 
  List, 
  Clock, 
  MapPin, 
  Eye, 
  X, 
  CheckCircle2, 
  AlertTriangle,
  User,
    BarChart2,
  Plus,
  ArrowRight,
  CalendarCheck,
  Handshake,
  Calendar as CalendarIcon,
  Tag,
  StickyNote,
  Info
} from 'lucide-react';
import { Badge } from '../common/design-system/Badge';
import Button from '../common/design-system/Button';
import clsx from 'clsx';
import styles from './BusinessCalendar.module.scss'; // Phase 5 new styles

// Configure moment locale
moment.locale('es');
const localizer = momentLocalizer(moment);

// Animation variants following FOTH design system
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1, scale: 1, y: 0,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
  },
  exit: {
    opacity: 0, scale: 0.95, y: 20,
    transition: { duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const BusinessCalendar = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [reservations, setReservations] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewType, setViewType] = useState('calendar');
  const [showSidebar, setShowSidebar] = useState(true);
  const [confirmingReservation, setConfirmingReservation] = useState(false);
  const [reservationActionError, setReservationActionError] = useState('');
  const confirmReservation = async () => {
    if (!selectedEvent) return;
    setConfirmingReservation(true);
    setReservationActionError('');
    try {
      const response = await api.post(`/reservations/${selectedEvent.id}/update_status/`, { status: 'confirmed' });
      setReservations(previous => previous.map(item => item.id === selectedEvent.id ? response.data : item));
      setSelectedEvent(response.data);
    } catch (err) {
      setReservationActionError(err.response?.data?.error || err.response?.data?.detail || 'No se pudo confirmar la reserva.');
    } finally {
      setConfirmingReservation(false);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await api.get('/collabs/collab/');
      const data = response.data;
      const offersData = Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
      setOffers(offersData);
    } catch (err) {
      console.error('Error fetching offers:', err);
    }
  };
  
  const fetchReservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startOfMonth = moment(currentDate).startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment(currentDate).endOf('month').format('YYYY-MM-DD');

      try {
        const allReservationsResponse = await api.get('/reservations/test-calendar/');
        const allData = allReservationsResponse.data;
        if (allData.reservations && allData.reservations.length > 0) {
          const filteredReservations = allData.reservations.filter(reservation => {
            const reservationDate = moment(reservation.start_datetime);
            return reservationDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
          });
          setReservations(filteredReservations);
          setLoading(false);
          return;
        }
      } catch (testErr) {
        console.log('Endpoint alternativo no disponible, usando endpoint principal', testErr);
      }

      let url = `/reservations/?start_date=${startOfMonth}&end_date=${endOfMonth}&page_size=1000`;
      if (selectedOffer) url += `&offer_id=${selectedOffer}`;
      
      const response = await api.get(url);
      const data = response.data;
      const reservationsData = Array.isArray(data) ? data : (data.results || []);
      setReservations(reservationsData);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError(`No se pudieron cargar las reservaciones: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [currentDate, selectedOffer]);

  useEffect(() => {
    if (currentUser && currentUser.user_type === 'business') {
      fetchOffers();
      fetchReservations();
    }
  }, [currentUser, fetchReservations]);

  const handleNavigate = useCallback((newDate) => {
    const newMonth = moment(newDate).month();
    const currentMonth = moment(currentDate).month();
    if (newMonth !== currentMonth) {
      setCurrentDate(newDate);
      fetchReservations();
    }
  }, [currentDate, fetchReservations]);

  const calendarEvents = useMemo(() => {
    if (!Array.isArray(reservations)) return [];
    
    return reservations.map(reservation => {
      const creatorName = reservation.creator_details?.username || 
                         reservation.creator_details?.full_name || 'Creator';
      
      const startTime = moment(reservation.start_datetime).format('HH:mm');
      let eventTitle = `${startTime} • ${creatorName}`;
      
      if (reservation.status === 'cancelled') eventTitle = `❌ ${eventTitle}`;
      
      return {
        id: reservation.id,
        title: eventTitle,
        start: new Date(reservation.start_datetime),
        end: new Date(reservation.end_datetime),
        status: reservation.status,
        allDay: false,
        resource: reservation
      };
    });
  }, [reservations]);

  const statistics = useMemo(() => {
    const total = reservations.length;
    const confirmed = reservations.filter(r => r.status === 'confirmed').length;
    const cancelled = reservations.filter(r => r.status === 'cancelled').length;
    
    const thisMonth = reservations.filter(r => 
      moment(r.start_datetime).isSame(currentDate, 'month')
    ).length;
    
    const nextWeek = reservations.filter(r => 
      moment(r.start_datetime).isBetween(moment(), moment().add(7, 'days'))
    ).length;

    return { total, confirmed, cancelled, thisMonth, nextWeek };
  }, [reservations, currentDate]);

  const upcomingReservations = useMemo(() => {
    return reservations
      .filter(r => moment(r.start_datetime).isAfter(moment()) && r.status === 'confirmed')
      .sort((a, b) => moment(a.start_datetime).diff(moment(b.start_datetime)))
      .slice(0, 3);
  }, [reservations]);

  const eventStyleGetter = (event) => {
    let style = {
      borderRadius: '6px',
      opacity: 0.9,
      color: '#ffffff',
      border: 'none',
      display: 'block',
      fontWeight: '500',
      fontSize: '0.85rem',
      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    };
    
    switch (event.status) {
      case 'confirmed':
        style.background = 'linear-gradient(135deg, #d4af37, #c9a96e)'; 
        style.color = '#ffffff';
        break;
      case 'cancelled':
        style.background = 'linear-gradient(135deg, #ef4444, #dc2626)'; 
        style.color = '#ffffff';
        style.opacity = 0.6; 
        style.textDecoration = 'line-through'; 
        break;
      default:
        style.background = 'linear-gradient(135deg, #d4af37, #c9a96e)'; 
    }
    return { style };
  };

  const handleEventSelect = (event) => setSelectedEvent(event.resource);
  const handleCloseModal = () => setSelectedEvent(null);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('D [de] MMMM [de] YYYY, HH:mm');
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'confirmed': return 'Confirmada';
      case 'cancelled': return 'Cancelada';
      default: return status || 'Desconocido';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle2 size={16} />;
      case 'pending': return <AlertTriangle size={16} />;
      case 'cancelled': return <X size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const handleMiniCalendarClick = (date) => setCurrentDate(date);

  // Added logic to properly resolve the mini calendar dates without missing dependencies
  const generateMiniCalendarDays = useCallback(() => {
    const startOfMonth = moment(currentDate).startOf('month');
    const endOfMonth = moment(currentDate).endOf('month');
    const startDate = moment(startOfMonth).startOf('week');
    const endDate = moment(endOfMonth).endOf('week');
    
    const days = [];
    const current = moment(startDate);
    
    while (current.isSameOrBefore(endDate)) {
      const dayReservations = reservations.filter(r => 
        moment(r.start_datetime).isSame(current, 'day')
      );
      
      days.push({
        date: moment(current),
        isCurrentMonth: current.isSame(currentDate, 'month'),
        isToday: current.isSame(moment(), 'day'),
        isSelected: current.isSame(currentDate, 'day'),
        hasReservations: dayReservations.length > 0,
        reservationCount: dayReservations.length,
        confirmedCount: dayReservations.filter(r => r.status === 'confirmed').length,
        pendingCount: dayReservations.filter(r => r.status === 'pending').length
      });
      
      current.add(1, 'day');
    }
    
    return days;
  }, [currentDate, reservations]);

  const miniCalendarDays = useMemo(() => generateMiniCalendarDays(), [generateMiniCalendarDays]);

  const handleCreateOffer = () => navigate('/business/offers/create');
  const handleViewAllReservations = () => { setViewType('list'); setSelectedOffer(''); };
  const handleViewAllOffers = () => navigate('/business/offers');

  if (loading) {
    return (
      <div className={styles['calendar-page']}>
        <div className={styles.container}>
          <motion.div 
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Clock className="animate-spin text-brand-gold mb-4" size={32} />
            <p>Cargando reservaciones...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['calendar-page']}>
        <div className={styles.container}>
          <motion.div 
            className={styles['empty-state']}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <AlertTriangle size={48} className="text-warning mx-auto mb-4" />
            <h3>Error al cargar las reservaciones</h3>
            <p>{error}</p>
            <Button variant="primary" onClick={fetchReservations}>Intentar de nuevo</Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className={styles['calendar-page']}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className={styles.container}>
        {/* Hero Section */}
        <motion.div className={styles['hero-section']} variants={itemVariants}>
          <div className={styles['badge-calendar']}>
            <CalendarDays size={16} />
            <span>Reservaciones Hub</span>
          </div>
          <h1>Calendario de Colaboraciones</h1>
          <p>Gestiona, supervisa y aprueba las reservaciones de todas tus colaboraciones activas en FOTH.</p>
        </motion.div>

        {/* Layout */}
        <div className={styles['calendar-layout']}>
          {/* Sidebar */}
          <AnimatePresence>
            {showSidebar && (
              <motion.div 
                className={styles.sidebar}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                variants={itemVariants}
              >
                {/* Stats */}
                <div className={styles['sidebar-card']}>
                  <div className={styles['sidebar-header']}>
                    <h3>Estadísticas del Mes</h3>
                  </div>
                  <div className={styles['stats-grid']}>
                    <div className={styles['stat-box']}>
                      <div className={clsx(styles['stat-icon'], styles.confirmed)}><CheckCircle2 size={16} /></div>
                      <span className={styles['stat-num']}>{statistics.confirmed}</span>
                      <span className={styles['stat-label']}>Confirmadas</span>
                    </div>
                    <div className={styles['stat-box']}>
                      <div className={clsx(styles['stat-icon'], styles.cancelled)}><X size={16} /></div>
                      <span className={styles['stat-num']}>{statistics.cancelled}</span>
                      <span className={styles['stat-label']}>Canceladas</span>
                    </div>
                    <div className={styles['stat-box']}>
                      <div className={clsx(styles['stat-icon'], styles.total)}><Handshake size={16} /></div>
                      <span className={styles['stat-num']}>{statistics.total}</span>
                      <span className={styles['stat-label']}>Total</span>
                    </div>
                    <div className={styles['stat-box']}>
                      <div className={clsx(styles['stat-icon'], styles.upcoming)}><CalendarIcon size={16} /></div>
                      <span className={styles['stat-num']}>{statistics.nextWeek}</span>
                      <span className={styles['stat-label']}>Esta Semana</span>
                    </div>
                  </div>
                </div>

                {/* Upcoming */}
                {upcomingReservations.length > 0 && (
                  <div className={styles['sidebar-card']}>
                    <div className={styles['sidebar-header']}>
                      <h3>Próximas Reservas</h3>
                    </div>
                    <div className={styles['upcoming-list']}>
                      {upcomingReservations.map(reservation => (
                        <div key={reservation.id} className={styles['upcoming-item']}>
                          <div className={styles['upcoming-date']}>
                            {moment(reservation.start_datetime).format('DD MMM')}
                          </div>
                          <div className={styles['upcoming-content']}>
                            <h4>{reservation.offer_details?.title}</h4>
                            <p>{reservation.creator_name}</p>
                            <span className={styles['upcoming-time']}>
                              {moment(reservation.start_datetime).format('HH:mm')}
                            </span>
                          </div>
                        </div>
                      ))}
                      {upcomingReservations.length >= 3 && (
                        <Button variant="secondary" onClick={handleViewAllReservations} className="mt-4 w-full">
                          Ver todas <ArrowRight size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Mini Calendar */}
                <div className={styles['sidebar-card']}>
                  <div className={clsx(styles['sidebar-header'], "mb-2")}>
                    <h3>Navegación Rápida</h3>
                  </div>
                  <div className={styles['mini-calendar']}>
                    <div className={styles['mini-calendar-header']}>
                      <button onClick={() => handleMiniCalendarClick(moment(currentDate).subtract(1, 'month').toDate())}>‹</button>
                      <span>{moment(currentDate).format('MMMM YYYY')}</span>
                      <button onClick={() => handleMiniCalendarClick(moment(currentDate).add(1, 'month').toDate())}>›</button>
                    </div>
                    <div className={styles['mini-calendar-grid']}>
                      {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map(day => (
                        <div key={day} className={styles['mini-day-header']}>{day}</div>
                      ))}
                      {miniCalendarDays.map((day, index) => (
                        <div
                          key={index}
                          className={clsx(
                            styles['mini-day'],
                            day.isCurrentMonth ? styles['current-month'] : styles['other-month'],
                            day.isToday && styles.today,
                            day.isSelected && styles.selected
                          )}
                          onClick={() => handleMiniCalendarClick(day.date.toDate())}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleMiniCalendarClick(day.date.toDate());
                            }
                          }}
                          title={day.hasReservations ? `${day.reservationCount} reserva(s)` : day.date.format('D [de] MMMM')}
                        >
                          <span>{day.date.format('D')}</span>
                          {day.hasReservations && (
                            <div className={clsx(styles['mini-indicator'], day.confirmedCount > 0 ? styles.confirmed : styles.pending)}></div>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full mt-4" onClick={() => handleMiniCalendarClick(new Date())}>
                      Ir a Hoy
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main */}
          <div className={styles['main-content']}>
            <motion.div className={styles['controls-section']} variants={itemVariants}>
              <div className={styles['view-toggle']}>
                <button
                  className={clsx(styles['tab-btn'], viewType === 'calendar' && styles.active)}
                  onClick={() => setViewType('calendar')}
                >
                  <CalendarDays size={16} /> Calendario
                </button>
                <button
                  className={clsx(styles['tab-btn'], viewType === 'list' && styles.active)}
                  onClick={() => setViewType('list')}
                >
                  <List size={16} /> Lista
                </button>
                <button
                  className={styles['tab-btn']}
                  onClick={() => setShowSidebar(!showSidebar)}
                >
                  <BarChart2 size={16} /> Panel Extra
                </button>
              </div>

              <div className="flex gap-4">
                <select
                  value={selectedOffer}
                  onChange={(e) => setSelectedOffer(e.target.value)}
                  className="px-4 py-2 border rounded-full text-sm font-medium bg-white text-secondary outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors"
                >
                  <option value="">Todas las colaboraciones</option>
                  {Array.isArray(offers) && offers.map(o => (
                    <option key={o.id} value={o.id}>{o.title || 'Sin título'}</option>
                  ))}
                </select>
                <Button variant="primary" onClick={handleCreateOffer}>
                  <Plus size={16} /> Nueva
                </Button>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex-1">
              {viewType === 'calendar' ? (
                <div className={styles['calendar-card']}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={handleEventSelect}
                    date={currentDate}
                    onNavigate={handleNavigate}
                    views={['month', 'week', 'day', 'agenda']}
                    messages={{
                      next: "Siguiente", previous: "Anterior", today: "Hoy",
                      month: "Mes", week: "Semana", day: "Día", agenda: "Agenda",
                      date: "Fecha", time: "Hora", event: "Evento",
                      noEventsInRange: "No hay reservas en este período"
                    }}
                  />
                </div>
              ) : (
                <div className={styles['reservations-list']}>
                  {reservations.length === 0 ? (
                    <div className={styles['empty-state']}>
                      <div className={styles['empty-icon']}><CalendarCheck /></div>
                      <h3>No hay reservaciones</h3>
                      <p>Las reservaciones de tus colaboraciones aparecerán aquí cuando los creators las soliciten.</p>
                      <div className={styles['empty-actions']}>
                        <Button variant="primary" onClick={handleCreateOffer}><Plus size={16} /> Crear Nueva Oferta</Button>
                        <Button variant="secondary" onClick={handleViewAllOffers}><Eye size={16} /> Ver Mis Ofertas</Button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles['reservations-grid']}>
                      {reservations.map(reservation => (
                        <div
                          key={reservation.id}
                          className={styles['reservation-card']}
                          onClick={() => setSelectedEvent(reservation)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedEvent(reservation);
                            }
                          }}
                        >
                          <div className={styles['card-header']}>
                            <h4>{reservation.offer_details?.title || 'Reservation'}</h4>
                            <Badge variant={getStatusVariant(reservation.status)}>
                              {getStatusLabel(reservation.status)}
                            </Badge>
                          </div>
                          
                          <div className={styles['card-content']}>
                            <div className={styles['info-item']}><User size={16} /><span>{reservation.creator_name || 'Creator'}</span></div>
                            <div className={styles['info-item']}><Clock size={16} /><span>{formatDateTime(reservation.start_datetime)}</span></div>
                            {reservation.notes && <div className={styles['info-item']}><MapPin size={16} /><span>{reservation.notes}</span></div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            className={styles['modal-overlay']}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className={styles['reservation-modal']}
              variants={modalVariants} initial="hidden" animate="visible" exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <button className={styles['btn-close']} onClick={handleCloseModal}><X size={16} /></button>
              
              <div className={styles['modal-header']}>
                <h3>{selectedEvent.offer_details?.title || 'Detalles de la Reserva'}</h3>
                <Badge variant={getStatusVariant(selectedEvent.status)}>
                  {getStatusIcon(selectedEvent.status)} {getStatusLabel(selectedEvent.status)}
                </Badge>
              </div>
              
              <div className={styles['modal-content']}>
                <div className={styles['detail-row']}>
                  <User className={styles['detail-icon']} />
                  <div className={styles['detail-info']}>
                    <span className={styles['detail-label']}>Creator</span>
                    <span className={styles['detail-value']}>
                      {selectedEvent.creator_details?.full_name || selectedEvent.creator_details?.username || 'Creator'}
                    </span>
                  </div>
                </div>
                
                <div className={styles['detail-row']}>
                  <Clock className={styles['detail-icon']} />
                  <div className={styles['detail-info']}>
                    <span className={styles['detail-label']}>Horario</span>
                    <span className={styles['detail-value']}>
                      {formatDateTime(selectedEvent.start_datetime)} - {moment(selectedEvent.end_datetime).format('HH:mm')}
                    </span>
                  </div>
                </div>

                <div className={styles['detail-row']}>
                  <Tag className={styles['detail-icon']} />
                  <div className={styles['detail-info']}>
                    <span className={styles['detail-label']}>Mecánica</span>
                    <span className={styles['detail-value']}>{selectedEvent.offer_details?.type_display || selectedEvent.offer_details?.type || 'Standard'}</span>
                  </div>
                </div>

                {selectedEvent.notes && (
                  <div className={styles['detail-row']}>
                    <StickyNote className={styles['detail-icon']} />
                    <div className={styles['detail-info']}>
                      <span className={styles['detail-label']}>Notas</span>
                      <span className={styles['detail-value']}>{selectedEvent.notes}</span>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex gap-3 text-sm text-gray-400 bg-white/5 p-4 rounded-xl items-center">
                  <Info size={16} className="text-brand-gold flex-shrink-0" />
                  <p>Las solicitudes nuevas quedan pendientes. Primero aprueba la postulación del creador y después confirma su reserva.</p>
                  {selectedEvent.status === 'pending' && (
                    <Button onClick={confirmReservation} loading={confirmingReservation}>Confirmar reserva</Button>
                  )}
                  {reservationActionError && <p role="alert">{reservationActionError}</p>}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default BusinessCalendar;
